import React, { useEffect, useMemo, useState, useCallback, useRef } from 'react';
import styled from 'styled-components';
import tw from 'twin.macro';
import { useMutation, useQuery } from '@apollo/client';
import { toast } from 'react-toastify';
import { useDropzone } from 'react-dropzone';
import { Button } from '../button';
import { Modal } from '../modal';
import { DLVerification } from '../DLVerification';
import { URLS } from '../../../constants/urls';
import DriverLicenseEgImg from '../../../assets/images/DriverLicenseEg.png';
import {
  COMPLETE_GUEST_CHECKIN,
  COMPLETE_HOST_CHECKIN,
  GET_PRESIGNED_URL_FOR_BOOKING,
  UPLOAD_TRIP_PHOTO,
  DELETE_TRIP_PHOTO,
} from '../../services/bookingService/queries';
import { useBookingLocale } from '../../hooks/useBookingLocale';
import { BOOKING_POLICY_RECORD } from '../../services/policyService/queries';

const Container = styled.div`
  ${tw`flex flex-col gap-4 w-full`}
`;

const Title = styled.h2`
  ${tw`text-xl font-semibold text-text-primary`}
`;

const StepHeader = styled.div`
  ${tw`text-sm text-text-secondary`}
`;

const Section = styled.div`
  ${tw`flex flex-col gap-3`}
`;

const Label = styled.label`
  ${tw`text-sm font-medium text-text-secondary`}
`;

const Input = styled.input`
  ${tw`w-full p-3 border border-border-default rounded-lg`}
`;

const HelperText = styled.p`
  ${tw`text-xs text-text-secondary`}
`;

const Actions = styled.div`
  ${tw`flex flex-col md:flex-row gap-3 justify-end`}
`;

const DropzoneContainer = styled.div<{ isDragActive: boolean }>`
  ${tw`border-2 border-dashed rounded-lg p-8 text-center cursor-pointer transition-colors`}
  border-color: ${({ isDragActive }) => (isDragActive ? '#4F46E5' : 'var(--color-border)')};
  background-color: ${({ isDragActive }) => (isDragActive ? '#EEF2FF' : 'white')};

  &:hover {
    ${tw`border-black`}
  }

  @media (max-width: 768px) {
    ${tw`p-5`}
  }

  @media (max-width: 480px) {
    ${tw`p-4`}
  }
`;

const DropzoneText = styled.p`
  ${tw`text-sm text-text-secondary mt-2`}
`;

const PhotoGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(120px, 1fr));
  gap: 12px;
  margin-top: 16px;
  max-height: 320px;
  overflow-y: auto;
  padding: 8px;

  @media (max-width: 768px) {
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: 10px;
    max-height: 280px;
  }

  @media (max-width: 480px) {
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: 8px;
    max-height: 240px;
  }
`;

const PhotoPreview = styled.div`
  ${tw`relative aspect-square rounded-lg overflow-hidden border border-border-default`}
`;

const PhotoImg = styled.img`
  ${tw`w-full h-full object-cover`}
`;

const RemoveButton = styled.button`
  ${tw`absolute top-1 right-1 bg-red-500 text-white rounded-full w-6 h-6 flex items-center justify-center text-xs font-bold hover:bg-red-600`}
`;

const StatusText = styled.p`
  ${tw`text-sm text-text-secondary`}
`;

const DLSection = styled(Section)`
  ${tw`flex flex-col items-center justify-center flex-1`}
`;

const DLExampleImageStyled = styled.img`
  width: 280px;
  height: 175px;
  object-fit: contain;
  margin-bottom: 24px;
`;

const DLHelperHint = styled(HelperText)`
  margin-top: 12px;
`;

const StartButtonRow = styled.div`
  ${tw`flex justify-center mt-4`}
`;

const StartTripButton = styled.button`
  ${tw`font-semibold text-base cursor-pointer border-none`}
  background-color: #eb5a1c;
  color: #fff;
  padding: 16px 0;
  width: 80%;
  border-radius: 16px;
  margin-bottom: 30px;
  border: 2px solid #eb5a1c;
  &:hover {
    background-color: #d4510f;
  }
`;

const PhotoCountText = styled.p`
  ${tw`text-sm text-text-secondary font-medium mt-2`}
`;

const TransitionContainer = styled.div`
  ${tw`flex flex-col`}
  min-height: 400px;
`;

const TransitionHero = styled.div`
  ${tw`flex items-center justify-center`}
  height: 200px;
  background-color: #e8f5e9;
`;

const TransitionContent = styled.div`
  ${tw`flex flex-col items-center justify-center flex-1 px-8 py-6`}
`;

const TransitionTitle = styled.h2`
  ${tw`text-xl font-bold text-text-primary text-center mb-3`}
`;

const TransitionDescription = styled.p`
  ${tw`text-base text-text-secondary text-center leading-relaxed max-w-sm`}
`;

const TransitionActions = styled.div`
  ${tw`flex gap-3 px-6 pb-6`}
`;

const TransitionSecondaryBtn = styled.button`
  ${tw`flex-1 py-3 rounded-card font-semibold text-base cursor-pointer`}
  border: 2px solid var(--color-border);
  background-color: #fff;
  color: var(--color-text-secondary);
  &:hover {
    background-color: var(--color-bg-secondary);
  }
`;

const TransitionPrimaryBtn = styled.button`
  ${tw`flex-1 py-3 rounded-card font-semibold text-base cursor-pointer border-none`}
  background-color: #eb5a1c;
  color: #fff;
  &:hover {
    background-color: #d4510f;
  }
`;

interface StartTripCheckinModalProps {
  isOpen: boolean;
  onClose: () => void;
  booking: any;
  isHost: boolean;
  onCompleted?: () => void;
  refetchBooking: () => void;
}

const MIN_PHOTOS = 6;

export const StartTripCheckinModal = ({
  isOpen,
  onClose,
  booking,
  isHost,
  onCompleted,
  refetchBooking,
}: StartTripCheckinModalProps) => {
  // Spec 028: the odometer is read in the trip's unit.
  const { distanceUnit } = useBookingLocale(booking?.id, isHost ? 'host' : 'guest');
  const km = distanceUnit === 'km';
  const [stepIndex, setStepIndex] = useState(0);
  const verifyFnRef = useRef<(() => void) | null>(null);
  const [preTripMileage, setPreTripMileage] = useState('');
  const [preTripFuelLevel, setPreTripFuelLevel] = useState('');
  const [isUploading, setIsUploading] = useState(false);
  const [selectedFiles, setSelectedFiles] = useState<File[]>([]);
  const [filePreviews, setFilePreviews] = useState<string[]>([]);
  const [uploadProgress, setUploadProgress] = useState<{ [key: number]: number }>({});

  const [getPresignedUrl] = useMutation(GET_PRESIGNED_URL_FOR_BOOKING);
  const [uploadTripPhoto] = useMutation(UPLOAD_TRIP_PHOTO);
  const [deleteTripPhoto] = useMutation(DELETE_TRIP_PHOTO, {
    refetchQueries: [],
  });
  const [completeHostCheckin, { loading: completingHost }] = useMutation(COMPLETE_HOST_CHECKIN);
  const [completeGuestCheckin, { loading: completingGuest }] = useMutation(COMPLETE_GUEST_CHECKIN);
  const driverAcceptances = useQuery(BOOKING_POLICY_RECORD, {
    variables: { id: booking?.id ?? '' },
    skip: !isOpen || !isHost || !booking?.id,
    fetchPolicy: 'network-only',
    errorPolicy: 'all',
  });
  const waitingDrivers = (
    driverAcceptances.data?.booking?.additionalDriverAcceptances ?? []
  ).filter((row: { accepted?: boolean }) => row.accepted === false);

  const photoCount = booking?.tripStartImages?.length ?? 0;
  const hasValidDL =
    booking?.dlVerificationStatus === 'VERIFIED' || booking?.dlVerificationStatus === 'SUBMITTED';
  const requiresDL = !isHost && !hasValidDL;

  const steps = useMemo(() => {
    if (isHost) {
      return ['photos', 'mileage'];
    }
    return requiresDL ? ['dl', 'dl-transition', 'photos'] : ['photos'];
  }, [isHost, requiresDL]);

  useEffect(() => {
    if (isOpen) {
      setStepIndex(0);
      setPreTripMileage('');
      setPreTripFuelLevel('');
      setSelectedFiles([]);
      setFilePreviews([]);
      setUploadProgress({});
    }
  }, [isOpen]);

  // Clean up preview URLs when component unmounts or files change
  useEffect(() => {
    return () => {
      filePreviews.forEach(url => URL.revokeObjectURL(url));
    };
  }, [filePreviews]);

  // Auto-advance past DL step when DL becomes valid (after submission + refetch)
  useEffect(() => {
    if (isOpen && hasValidDL && steps[stepIndex] === 'dl') {
      setStepIndex(prev => Math.min(prev + 1, steps.length - 1));
    }
  }, [isOpen, hasValidDL, steps, stepIndex]);

  // Clamp stepIndex if steps array shrunk (e.g. DL step removed after submission)
  const clampedStepIndex = Math.min(stepIndex, steps.length - 1);
  const currentStep = steps[clampedStepIndex];
  const isLastStep = clampedStepIndex === steps.length - 1;
  const totalPhotoCount = photoCount + selectedFiles.length;
  const canProceedFromPhotos =
    totalPhotoCount >= MIN_PHOTOS && !isUploading && selectedFiles.length === 0;

  const onDrop = useCallback((acceptedFiles: File[]) => {
    setSelectedFiles(prev => [...prev, ...acceptedFiles]);
    const newPreviews = acceptedFiles.map(file => URL.createObjectURL(file));
    setFilePreviews(prev => [...prev, ...newPreviews]);
  }, []);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: { 'image/*': ['.jpeg', '.jpg', '.png', '.webp'] },
    multiple: true,
    disabled: isUploading,
  });

  const handleRemovePhoto = (index: number) => {
    const newFiles = selectedFiles.filter((_, i) => i !== index);
    const newPreviews = filePreviews.filter((_, i) => i !== index);

    // Revoke URL for removed preview
    URL.revokeObjectURL(filePreviews[index]);

    setSelectedFiles(newFiles);
    setFilePreviews(newPreviews);
  };

  const uploadFileToS3 = (
    file: File,
    index: number,
    s3Url: string,
    s3Key: string,
  ): Promise<string> => {
    return new Promise((resolve, reject) => {
      const xhr = new XMLHttpRequest();
      xhr.open('PUT', s3Url);
      xhr.setRequestHeader('Content-Type', 'image/jpeg');
      xhr.upload.onprogress = event => {
        if (event.lengthComputable) {
          const percentComplete = Math.round((event.loaded * 100) / event.total);
          setUploadProgress(prev => ({ ...prev, [index]: percentComplete }));
        }
      };
      xhr.onload = () => {
        if (xhr.status >= 200 && xhr.status < 300) resolve(s3Key);
        else reject(new Error(`Upload failed with status ${xhr.status}`));
      };
      xhr.onerror = () => reject(new Error('Network error'));
      xhr.send(file);
    });
  };

  const handleUploadNow = async () => {
    if (selectedFiles.length === 0) {
      return;
    }

    const filesToUpload = selectedFiles.length;
    setIsUploading(true);
    setUploadProgress({});

    try {
      // Upload sequentially to avoid overwriting tripStartImages with concurrent updates.
      for (let index = 0; index < selectedFiles.length; index += 1) {
        const file = selectedFiles[index];
        const { data } = await getPresignedUrl({
          variables: {
            fileType: 'image/jpeg',
            bookingId: booking.id,
            imageType: 'tripStart',
          },
        });

        const { url, key } = data.getPresignedUrlForBooking;

        await uploadFileToS3(file, index, url, key);

        await uploadTripPhoto({
          variables: {
            input: {
              bookingId: booking.id,
              imageUrl: key,
              imageType: 'tripStart',
              photoTimestamp: new Date(),
            },
          },
        });
      }

      // Clean up previews only - keep files for now, they will be cleared after refetch
      filePreviews.forEach(url => URL.revokeObjectURL(url));

      // Clear selected files after successful upload
      setSelectedFiles([]);
      setFilePreviews([]);
      setUploadProgress({});

      await refetchBooking();
      toast.success(`Successfully uploaded ${filesToUpload} photo(s)`);
    } catch (error: any) {
      toast.error(error.message || 'Failed to upload photos');
    } finally {
      setIsUploading(false);
    }
  };

  const handleDeleteUploadedPhoto = async (imageUrl: string, index: number) => {
    try {
      await deleteTripPhoto({
        variables: {
          input: {
            bookingId: booking.id,
            imageUrl: imageUrl,
            imageType: 'tripStart',
          },
        },
      });

      await refetchBooking();
      toast.success('Photo deleted');
    } catch (error: any) {
      toast.error(error.message || 'Failed to delete photo');
    }
  };

  const handleNext = async () => {
    if (currentStep === 'photos' && !canProceedFromPhotos) {
      toast.error(`Please upload at least ${MIN_PHOTOS} photos`);
      return;
    }

    if (currentStep === 'dl' && !hasValidDL) {
      toast.error('Please complete DL verification before proceeding');
      return;
    }

    if (isLastStep) {
      if (isHost) {
        const mileageValue = Number(preTripMileage);
        const fuelValue = Number(preTripFuelLevel);

        if (!Number.isFinite(mileageValue) || mileageValue <= 0) {
          toast.error('Please enter a valid pre-trip mileage');
          return;
        }

        if (!Number.isFinite(fuelValue) || fuelValue < 0 || fuelValue > 100) {
          toast.error('Fuel level must be between 0 and 100');
          return;
        }

        try {
          await completeHostCheckin({
            variables: {
              input: {
                bookingId: booking.id,
                preTripMileage: Math.round(mileageValue),
                preTripFuelLevel: Math.round(fuelValue),
              },
            },
          });
          await refetchBooking();
          toast.success('Host check-in completed');
          onCompleted?.();
          onClose();
        } catch (error: any) {
          toast.error(error.message || 'Failed to complete host check-in');
        }
        return;
      }

      if (!hasValidDL) {
        toast.error('Please complete DL verification before finishing check-in');
        return;
      }

      try {
        await completeGuestCheckin({
          variables: {
            input: {
              bookingId: booking.id,
            },
          },
        });
        await refetchBooking();
        toast.success('Guest check-in completed');
        onCompleted?.();
        onClose();
      } catch (error: any) {
        toast.error(error.message || 'Failed to complete guest check-in');
      }
      return;
    }

    setStepIndex(prev => Math.min(prev + 1, steps.length - 1));
  };

  const handleBack = () => {
    setStepIndex(prev => Math.max(prev - 1, 0));
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} modalWidth="min(680px, calc(100vw - 20px))">
      <Container>
        {currentStep !== 'dl-transition' && (
          <>
            <Title>Start Trip Check-in</Title>
            <StepHeader>
              Step {clampedStepIndex + 1} of {steps.length}
            </StepHeader>
            {waitingDrivers.map((row: { driverId: string; name: string }) => (
              <p key={row.driverId} data-testid="waiting-for-driver">
                Waiting for {row.name}
              </p>
            ))}
          </>
        )}

        {currentStep === 'dl' && (
          <DLSection>
            <DLExampleImageStyled src={DriverLicenseEgImg} alt="Driver's License Example" />
            <DLVerification
              bookingId={booking.id}
              isHost={false}
              dlVerificationStatus={booking.dlVerificationStatus}
              dlPhoto={booking.dlPhoto}
              dlSelfiePhoto={booking.dlSelfiePhoto}
              dlPhotoUrl={booking.dlPhotoUrl}
              dlSelfiePhotoUrl={booking.dlSelfiePhotoUrl}
              dlRejectionReason={booking.dlRejectionReason}
              dlSubmittedAt={booking.dlSubmittedAt}
              dlVerifiedAt={booking.dlVerifiedAt}
              tripStartTime={booking.startAt}
              totalTripAmount={booking.receipt?.totalPrice || 0}
              onUpdate={refetchBooking}
              hideButton
              onRegisterVerify={fn => {
                verifyFnRef.current = fn;
              }}
            />
            {!hasValidDL && <DLHelperHint>Submit your DL verification to continue.</DLHelperHint>}
          </DLSection>
        )}

        {currentStep === 'dl-transition' && (
          <TransitionContainer>
            <TransitionHero>
              <div style={{ textAlign: 'center' }}>
                <span style={{ fontSize: '64px' }}>&#10003;</span>
              </div>
            </TransitionHero>
            <TransitionContent>
              <TransitionTitle>Great! Now let's check your car</TransitionTitle>
              <TransitionDescription>
                Your ID is in review. Before handing over the keys, let's take a few quick photos of
                your car's exterior and interior to document its current condition.
              </TransitionDescription>
            </TransitionContent>
            <TransitionActions>
              <TransitionSecondaryBtn onClick={onClose}>Do it later</TransitionSecondaryBtn>
              <TransitionPrimaryBtn
                onClick={() => setStepIndex(prev => Math.min(prev + 1, steps.length - 1))}
              >
                Continue
              </TransitionPrimaryBtn>
            </TransitionActions>
          </TransitionContainer>
        )}

        {currentStep === 'photos' && (
          <Section>
            <Label>Trip start photos</Label>
            <HelperText>Please take at least {MIN_PHOTOS} photos to upload</HelperText>

            {totalPhotoCount > 0 && (
              <PhotoCountText>
                {totalPhotoCount} photo{totalPhotoCount !== 1 ? 's' : ''} selected
                {selectedFiles.length > 0 && ` (${selectedFiles.length} pending upload)`}
              </PhotoCountText>
            )}

            <DropzoneContainer {...getRootProps()} isDragActive={isDragActive}>
              <input {...getInputProps()} />
              <div>
                <p
                  style={{
                    fontSize: '16px',
                    fontWeight: 500,
                    color: '#4F46E5',
                    marginBottom: '8px',
                  }}
                >
                  {isDragActive ? 'Drop photos here' : 'Click or drag photos to select'}
                </p>
                <DropzoneText>
                  Select multiple photos at once. You can upload up to {MIN_PHOTOS} or more photos.
                </DropzoneText>
              </div>
            </DropzoneContainer>

            {(photoCount > 0 || selectedFiles.length > 0) && (
              <>
                <PhotoGrid>
                  {/* Display already uploaded photos */}
                  {booking?.tripStartImages?.map((image: any, index: number) => {
                    const imageUrl = image.url?.startsWith('http')
                      ? image.url
                      : `${URLS.S3_BUCKET.CAR_THUMBNAILS}/${image.url}`;

                    return (
                      <PhotoPreview key={`uploaded-${index}`}>
                        <PhotoImg src={imageUrl} alt={`Uploaded photo ${index + 1}`} />
                        <RemoveButton
                          onClick={e => {
                            e.stopPropagation();
                            handleDeleteUploadedPhoto(image.url, index);
                          }}
                          disabled={isUploading}
                        >
                          ×
                        </RemoveButton>
                      </PhotoPreview>
                    );
                  })}

                  {/* Display pending upload photos */}
                  {filePreviews.map((preview, index) => (
                    <PhotoPreview key={`pending-${index}`}>
                      <PhotoImg src={preview} alt={`Preview ${index + 1}`} />
                      <RemoveButton
                        onClick={e => {
                          e.stopPropagation();
                          handleRemovePhoto(index);
                        }}
                        disabled={isUploading}
                      >
                        ×
                      </RemoveButton>
                      {uploadProgress[index] !== undefined && (
                        <div
                          style={{
                            position: 'absolute',
                            bottom: 0,
                            left: 0,
                            right: 0,
                            height: '4px',
                            backgroundColor: 'var(--color-border)',
                          }}
                        >
                          <div
                            style={{
                              height: '100%',
                              width: `${uploadProgress[index]}%`,
                              backgroundColor: '#4F46E5',
                              transition: 'width 0.3s ease',
                            }}
                          />
                        </div>
                      )}
                    </PhotoPreview>
                  ))}
                </PhotoGrid>
                {selectedFiles.length > 0 && (
                  <div style={{ marginTop: '16px' }}>
                    <Button
                      text={isUploading ? 'Uploading...' : 'Upload Now'}
                      onClick={handleUploadNow}
                      isDisabled={isUploading}
                      isLoading={isUploading}
                      buttonWidth="200px"
                    />
                  </div>
                )}
              </>
            )}
          </Section>
        )}

        {currentStep === 'mileage' && (
          <Section>
            <Label>{km ? 'Start trip odometer (km)' : 'Start trip mileage (miles)'}</Label>
            <Input
              type="text"
              inputMode="numeric"
              pattern="[0-9]*"
              value={preTripMileage}
              onChange={event => {
                const value = event.target.value.trim();
                if (value === '') {
                  setPreTripMileage('');
                } else {
                  const numValue = parseInt(value);
                  if (!isNaN(numValue) && numValue >= 1) {
                    setPreTripMileage(value);
                  }
                }
              }}
              onFocus={e => {
                const target = e.target as HTMLInputElement;
                const length = target.value.length;
                target.setSelectionRange(length, length);
              }}
              placeholder={km ? 'Enter the odometer reading' : 'Enter odometer mileage'}
            />
            <Label>Starting fuel level (0–100%)</Label>
            <Input
              type="text"
              inputMode="numeric"
              pattern="[0-9]*"
              value={preTripFuelLevel}
              onChange={event => {
                const value = event.target.value.trim();
                if (value === '') {
                  setPreTripFuelLevel('');
                } else {
                  const numValue = parseInt(value);
                  if (!isNaN(numValue) && numValue >= 0 && numValue <= 100) {
                    setPreTripFuelLevel(value);
                  }
                }
              }}
              onFocus={e => {
                const target = e.target as HTMLInputElement;
                const length = target.value.length;
                target.setSelectionRange(length, length);
              }}
              placeholder="0 - 100"
            />
          </Section>
        )}

        {currentStep === 'dl' && !hasValidDL && (
          <StartButtonRow>
            <StartTripButton onClick={() => verifyFnRef.current?.()}>Start</StartTripButton>
          </StartButtonRow>
        )}
        {currentStep !== 'dl' && currentStep !== 'dl-transition' && (
          <Actions>
            {clampedStepIndex > 0 && <Button text="Back" theme="outlined" onClick={handleBack} />}
            <Button
              text={
                isLastStep
                  ? isHost
                    ? completingHost
                      ? 'Completing...'
                      : 'Complete Check-in'
                    : completingGuest
                      ? 'Completing...'
                      : 'Complete Check-in'
                  : 'Next'
              }
              onClick={handleNext}
              isDisabled={
                (currentStep === 'photos' && !canProceedFromPhotos) ||
                completingHost ||
                completingGuest
              }
              isLoading={completingHost || completingGuest}
            />
          </Actions>
        )}
      </Container>
    </Modal>
  );
};
